import { useEffect, useRef } from 'react';
import type { RoadTripWaypoint, RoadTripSegment } from '../types/roadTrip';

// Map component for displaying road trip routes

interface RoadTripMapProps {
  waypoints: RoadTripWaypoint[];
  segments: RoadTripSegment[];
}

export default function RoadTripMap({ waypoints, segments }: RoadTripMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);

  useEffect(() => {
    // Simple map initialization - would use Leaflet in real implementation
    if (!mapRef.current || waypoints.length === 0) return;

    // For now, show a placeholder map with waypoints
    const initMap = async () => {
      try {
        const L = await import('leaflet');
        await import('leaflet/dist/leaflet.css');

        if (mapInstanceRef.current) {
          mapInstanceRef.current.remove();
        }

        const map = L.map(mapRef.current!).setView(
          [waypoints[0].location.lat, waypoints[0].location.lng],
          5
        );

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors'
        }).addTo(map);

        // Add markers for waypoints
        waypoints.forEach((waypoint, index) => {
          const marker = L.marker([waypoint.location.lat, waypoint.location.lng])
            .addTo(map)
            .bindPopup(`
              <strong>${index + 1}. ${waypoint.name}</strong><br/>
              ${waypoint.location.name}<br/>
              Type: ${waypoint.type}
            `);

          // Custom marker colors
          if (index === 0) {
            marker.getElement()?.classList.add('leaflet-marker-icon-green');
          } else if (index === waypoints.length - 1) {
            marker.getElement()?.classList.add('leaflet-marker-icon-red');
          }
        });

        // Draw route lines between waypoints
        if (waypoints.length > 1) {
          const coordinates = waypoints.map(w => [w.location.lat, w.location.lng] as [number, number]);
          L.polyline(coordinates, { color: 'blue', weight: 3 }).addTo(map);
          map.fitBounds(L.latLngBounds(coordinates));
        }

        mapInstanceRef.current = map;
      } catch (error) {
        console.error('Failed to initialize map:', error);
      }
    };

    initMap();

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [waypoints]);

  if (waypoints.length === 0) {
    return (
      <div className="h-64 bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-2">🗺️</div>
          <p className="text-gray-500">Add stops to see your route</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-64" ref={mapRef} />
  );
}
